import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFacilities } from '../hooks/useFacilities';
import { toast } from 'sonner';
import { Plus, Minus, ShoppingBag, Search, X, User, Copy } from 'lucide-react';
import { todayIST, currentISTHour } from '../../utils/ist';

// Start hour from a slot_code ("0600-0730" → 6) — for past-checks and time-of-day grouping.
const slotStartHour = (slotId) => parseInt(String(slotId).slice(0, 2), 10);

/* Inline style helpers */
const s = {
  inp: {
    background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 8,
    padding: '9px 12px', fontSize: 13.5, outline: 'none', width: '100%', color: 'var(--ink)',
    transition: 'border-color .12s',
  },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  label: { fontSize: 12, color: 'var(--ink-2)', fontWeight: 500, display: 'block', marginBottom: 6 },
  sec:   { fontSize: 14, fontWeight: 600, letterSpacing: '-.01em', color: 'var(--ink)', margin: 0 },
  ink3:  { color: 'var(--ink-3)' },
  ink4:  { color: 'var(--ink-4)' },
  accent:{ color: 'var(--accent)' },
  brand: { color: 'var(--brand)' },
  mono:  { fontFamily: 'var(--f-mono)' },
};

export default function AdminCreateRentalBooking() {
  const { user, api } = useAuth();
  const navigate       = useNavigate();

  const { facilities } = useFacilities();
  const allowedLocations = (user?.role === 'owner'
    ? facilities
    : facilities.filter(f => user?.assigned_locations?.includes(f.location_id))
  ).map(f => ({ id: f.location_id, name: f.name }));

  const [form, setForm] = useState({
    customer_name: '', customer_phone: '', notes: '',
    location_id: null, date: todayIST(),
  });

  useEffect(() => {
    if (allowedLocations.length > 0 && form.location_id === null) {
      setForm(f => ({ ...f, location_id: allowedLocations[0].id }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedLocations.length]);

  const selectedFacility = facilities.find(f => f.location_id === form.location_id);
  const rentalCatalogue  = selectedFacility?.rental_items || [];
  const today            = todayIST();

  // ── Slot & rental data ───────────────────────────────────────────────────
  const [allSlots,     setAllSlots]     = useState([]);
  const [rentalUsage,  setRentalUsage]  = useState({});
  const [slotsLoading, setSlotsLoading] = useState(false);

  const fetchData = useCallback(() => {
    if (!form.location_id || !form.date) return;
    setSlotsLoading(true);
    api.get('/api/slots/location-availability', {
      params: { location_id: form.location_id, date: form.date },
    })
      .then(res => {
        const slotsMap = res.data.slots || {};
        // Rentals aren't tied to a court, so offer the union of every ground's
        // custom slots (grounds can have different slot lists), de-duped + sorted.
        const byCode = new Map();
        for (const slots of Object.values(slotsMap)) {
          for (const sl of (slots || [])) {
            if (!byCode.has(sl.id)) byCode.set(sl.id, { id: sl.id, time: sl.time, start_time: sl.start_time });
          }
        }
        const union = [...byCode.values()].sort((a, b) => (a.start_time || a.id).localeCompare(b.start_time || b.id));
        setAllSlots(union);
        setRentalUsage(res.data.rental_usage || {});
      })
      .catch(() => toast.error('Failed to load availability'))
      .finally(() => setSlotsLoading(false));
  }, [api, form.location_id, form.date]);

  useEffect(() => { setSelectedSlots(new Set()); setRentalMatrix({}); fetchData(); }, [fetchData]);

  const isSlotPast = useCallback((slotId) => {
    if (form.date > today) return false;
    if (form.date < today) return true;
    return slotStartHour(slotId) < currentISTHour();
  }, [form.date, today]);

  // ── Slot selection ───────────────────────────────────────────────────────
  const [selectedSlots, setSelectedSlots] = useState(new Set());
  const toggleSlot = (slotId) => {
    if (isSlotPast(slotId)) return;
    setSelectedSlots(prev => { const n=new Set(prev); n.has(slotId)?n.delete(slotId):n.add(slotId); return n; });
  };
  const uniqueSlotIds = [...selectedSlots].sort();
  const rentalHours   = uniqueSlotIds.length;

  // ── Customer search ──────────────────────────────────────────────────────
  const [searchQuery,     setSearchQuery]     = useState('');
  const [suggestions,     setSuggestions]     = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading,   setSearchLoading]   = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    if (searchQuery.length < 3) { setSuggestions([]); setSearchLoading(false); return; }
    setSearchLoading(true);
    const t = setTimeout(() => {
      api.get('/api/bookings/customers/search', { params: { q: searchQuery } })
        .then(r => { setSuggestions(r.data); setShowSuggestions(true); })
        .catch(() => {})
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => { clearTimeout(t); setSearchLoading(false); };
  }, [searchQuery, api]);

  useEffect(() => {
    const h = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const pickCustomer = (c) => {
    setForm(f => ({ ...f, customer_name: c.name, customer_phone: c.phone }));
    setSearchQuery(''); setSuggestions([]); setShowSuggestions(false);
  };

  // ── Per-slot rental matrix: { [slotId]: { [itemId]: qty } } ─────────────
  const [rentalMatrix, setRentalMatrix] = useState({});

  const getMatrixQty = (slotId, itemId) => rentalMatrix[slotId]?.[String(itemId)] ?? 0;

  const setMatrixQty = (slotId, itemId, qty) => {
    const id = String(itemId);
    setRentalMatrix(prev => ({
      ...prev,
      [slotId]: { ...(prev[slotId] || {}), [id]: Math.max(0, qty) },
    }));
  };

  // Copy a specific item's quantities from first selected slot to all slots
  const copyItemToAll = (itemId, qty) => {
    const id = String(itemId);
    setRentalMatrix(prev => {
      const next = { ...prev };
      for (const sid of uniqueSlotIds) {
        next[sid] = { ...(next[sid] || {}), [id]: qty };
      }
      return next;
    });
  };

  // Copy the entire first-slot row to all slots
  const copyRowToAll = (fromSlotId) => {
    const fromRow = rentalMatrix[fromSlotId] || {};
    setRentalMatrix(prev => {
      const next = { ...prev };
      for (const sid of uniqueSlotIds) {
        next[sid] = { ...fromRow };
      }
      return next;
    });
    toast.success('Copied to all hours');
  };

  // Effective stock per item per slot
  const slotStock = (slotId, item) => {
    const id   = String(item.item_id);
    const bucket = rentalUsage[slotId] || {};
    const used   = bucket[id] ?? bucket[item.item_id] ?? 0;
    return Math.max(0, (item.quantity ?? 99) - used);
  };

  // Rental totals
  const rentalTotal = useMemo(() => {
    let total = 0;
    for (const slotId of uniqueSlotIds) {
      for (const item of rentalCatalogue) {
        total += getMatrixQty(slotId, item.item_id) * item.price_per_session;
      }
    }
    return total;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rentalMatrix, uniqueSlotIds, rentalCatalogue]);

  // Custom one-off rentals (flat, not per-slot)
  const [customRentals, setCustomRentals] = useState([]);
  const addCustomRental    = () => setCustomRentals(p => [...p, { id: Date.now(), name: '', price: '', qty: 1 }]);
  const updateCustomRental = (id, f, v) => setCustomRentals(p => p.map(r => r.id===id ? {...r,[f]:v} : r));
  const removeCustomRental = (id) => setCustomRentals(p => p.filter(r => r.id!==id));
  const customTotal = customRentals.reduce((s,r) => s+(r.qty||1)*(parseFloat(r.price)||0)*Math.max(1,rentalHours), 0);
  const grandTotal  = rentalTotal + customTotal;

  // ── Payment ──────────────────────────────────────────────────────────────
  const [payment, setPayment] = useState({ method: 'Cash', amount: '', status: 'Pending', transaction_id: '' });

  // ── Submit ───────────────────────────────────────────────────────────────
  const [progress, setProgress] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedSlots.size === 0) { toast.error('Select at least one time slot'); return; }
    if (!form.customer_name.trim() || !form.customer_phone.trim()) { toast.error('Enter customer name and phone'); return; }

    // Build per-slot rental arrays
    const hasAnyRental = uniqueSlotIds.some(sid =>
      rentalCatalogue.some(item => getMatrixQty(sid, item.item_id) > 0)
    ) || customRentals.some(r => r.name.trim() && parseFloat(r.price) > 0);

    if (!hasAnyRental) { toast.error('Add at least one rental item'); return; }

    const paymentData = payment.amount
      ? { method: payment.method, amount: parseFloat(payment.amount), status: payment.status,
          transaction_id: payment.method==='UPI' ? payment.transaction_id : null }
      : null;

    const sessionId = crypto.randomUUID();
    setProgress({ done: 0, total: uniqueSlotIds.length });

    const results = await Promise.all(
      uniqueSlotIds.map(slotId => {
        const slotInfo = allSlots.find(s => s.id === slotId);
        const catalogueItems = rentalCatalogue
          .filter(item => getMatrixQty(slotId, item.item_id) > 0)
          .map(item => ({
            item_id: item.item_id, name: item.name,
            quantity: getMatrixQty(slotId, item.item_id),
            price_per_item: item.price_per_session,
            total: getMatrixQty(slotId, item.item_id) * item.price_per_session,
          }));
        const oneOffItems = customRentals.filter(r=>r.name.trim()&&parseFloat(r.price)>0).map(r=>({
          item_id: `custom-${r.id}`, name: r.name.trim(),
          quantity: r.qty||1, price_per_item: parseFloat(r.price), total: (r.qty||1)*parseFloat(r.price),
        }));
        const rentalsData = [...catalogueItems, ...oneOffItems];

        return api.post('/api/bookings/', {
          booking_type: 'rental', location_id: form.location_id,
          ground_id: '__rental__', date: form.date,
          slot_id: slotId, slot_time: slotInfo?.time || slotId,
          customer_name: form.customer_name.trim(), customer_phone: form.customer_phone.trim(),
          notes: form.notes || null, payment: paymentData,
          rentals: rentalsData.length > 0 ? rentalsData : null,
          session_id: sessionId,
        }).then(()=>'ok').catch(()=>'error');
      })
    );

    setProgress(null);
    const successCount = results.filter(r=>r==='ok').length;
    if (successCount > 0) {
      toast.success(`Rental booking created for ${successCount} hr${successCount>1?'s':''}`);
      navigate('/admin/bookings');
    } else {
      toast.error('Failed to create rental booking');
    }
  };

  const submitLabel = rentalHours === 0
    ? 'Select at least one time slot'
    : `Create Rental Booking · ${rentalHours} hr${rentalHours>1?'s':''}${grandTotal>0?` · ₹${grandTotal}`:''}`;

  // Slot label lookups
  const slotTime  = (slotId) => allSlots.find(s=>s.id===slotId)?.time || slotId;
  const slotStart = (slotId) => allSlots.find(s=>s.id===slotId)?.start_time || slotId;

  return (
    <div>
      <div className="sb-page-head">
        <div>
          <div className="sb-crumb">Operations · Bookings</div>
          <h1 style={{ display:'flex', alignItems:'center', gap:10 }}>
            <ShoppingBag size={22} style={s.accent} />
            New Rental Booking
          </h1>
          <div className="sb-sub">Equipment rental — no court booking required</div>
        </div>
        <div className="sb-page-actions">
          <button onClick={() => navigate('/admin/bookings')} className="sb-btn ghost">← Back</button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="l-two-col">

          {/* ── Left column ─────────────────────────────────────────────── */}
          <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

            {/* Customer */}
            <div className="sb-card" style={{ padding:'20px 22px', display:'flex', flexDirection:'column', gap:16 }}>
              <h2 style={s.sec}>Customer Details</h2>

              <div ref={searchRef} style={{ position:'relative' }}>
                <span style={{ ...s.label, display:'flex', alignItems:'center', gap:5 }}>
                  <Search size={12}/> Search past customers
                </span>
                <div style={{ position:'relative' }}>
                  <input type="text" value={searchQuery} onChange={e=>setSearchQuery(e.target.value)}
                    onFocus={() => suggestions.length>0 && setShowSuggestions(true)}
                    placeholder="Type name or phone…" style={{ ...s.inp, paddingRight:36 }} />
                  {searchLoading && (
                    <div style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)' }}>
                      <div style={{ width:14,height:14,borderRadius:'50%',border:'2px solid var(--line)',borderTopColor:'var(--accent)',animation:'spin .7s linear infinite' }} />
                    </div>
                  )}
                </div>
                {showSuggestions && suggestions.length > 0 && (
                  <div style={{ position:'absolute',zIndex:20,width:'100%',marginTop:4,background:'var(--surface)',border:'1px solid var(--line)',borderRadius:10,boxShadow:'var(--sh-2)',overflow:'hidden' }}>
                    {suggestions.map((c,i) => (
                      <button key={i} type="button" onClick={()=>pickCustomer(c)}
                        style={{ width:'100%',display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'none',border:'none',textAlign:'left',cursor:'pointer',borderBottom:i<suggestions.length-1?'1px solid var(--line-soft)':'none' }}
                        onMouseEnter={e=>e.currentTarget.style.background='var(--surface-2)'}
                        onMouseLeave={e=>e.currentTarget.style.background='none'}>
                        <User size={13} style={s.accent}/>
                        <span style={{ fontSize:13,fontWeight:600 }}>{c.name}</span>
                        <span style={{ fontSize:12,...s.ink3,marginLeft:'auto',...s.mono }}>{c.phone}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div style={s.row2}>
                <div className="sb-field">
                  <label>Name</label>
                  <input type="text" value={form.customer_name} onChange={e=>setForm({...form,customer_name:e.target.value})} style={s.inp} required />
                </div>
                <div className="sb-field">
                  <label>Phone</label>
                  <input type="tel" value={form.customer_phone} onChange={e=>setForm({...form,customer_phone:e.target.value})} style={s.inp} required />
                </div>
              </div>
              <div className="sb-field">
                <label>Notes <span style={{ ...s.ink4,fontWeight:400 }}>(optional)</span></label>
                <textarea rows={2} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}
                  placeholder="e.g. walk-in customer, deposit paid" style={{ ...s.inp, resize:'none' }} />
              </div>
            </div>

            {/* Location & date */}
            <div className="sb-card" style={{ padding:'20px 22px', display:'flex', flexDirection:'column', gap:16 }}>
              <h2 style={s.sec}>Schedule</h2>
              <div style={s.row2}>
                <div className="sb-field">
                  <label>Location</label>
                  <select value={form.location_id??''} onChange={e=>setForm({...form,location_id:parseInt(e.target.value)})} style={s.inp}>
                    {allowedLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div className="sb-field">
                  <label>Date</label>
                  <input type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} style={s.inp} />
                </div>
              </div>

              {/* Slot picker */}
              <div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                  <span style={s.label}>
                    Select rental hour(s)
                    <span style={{ ...s.ink4, fontWeight:400, marginLeft:6, fontSize:11 }}>each slot shows available stock</span>
                  </span>
                  {selectedSlots.size > 0 && (
                    <button type="button" onClick={()=>{setSelectedSlots(new Set());setRentalMatrix({});}} className="sb-btn sm ghost" style={s.ink3}>
                      <X size={10}/> Clear
                    </button>
                  )}
                </div>

                {slotsLoading ? (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
                    {Array.from({length:12}).map((_,i)=>(
                      <div key={i} style={{ height:72, background:'var(--line-soft)', borderRadius:9, opacity:.5 }} />
                    ))}
                  </div>
                ) : allSlots.length === 0 ? (
                  <p style={{ fontSize:13,...s.ink4,fontStyle:'italic' }}>No slots available for this location.</p>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap: 14 }}>
                    {[
                      { id:'morning',   label:'Morning',   test:(h)=>h<12 },
                      { id:'afternoon', label:'Afternoon', test:(h)=>h>=12&&h<17 },
                      { id:'evening',   label:'Evening',   test:(h)=>h>=17 },
                    ].map(period => {
                      const periodSlots = allSlots.filter(sl => period.test(slotStartHour(sl.start_time?.replace(':','') ?? sl.id)));
                      if (periodSlots.length === 0) return null;
                      const maxStock = rentalCatalogue.length ? Math.max(1, ...rentalCatalogue.map(i=>i.quantity||0)) : 1;
                      return (
                        <div key={period.id}>
                          <div className="sb-slot-group-lbl">{period.label}</div>
                          <div className="sb-slot-grid">
                            {periodSlots.map(slot => {
                              const isPast     = isSlotPast(slot.id);
                              const isSelected = selectedSlots.has(slot.id);
                              const stocks     = rentalCatalogue.map(item => slotStock(slot.id, item));
                              const lowest     = stocks.length ? Math.min(...stocks) : null;
                              const low = lowest!=null && lowest>0 && lowest<=3, empty = lowest===0;
                              const barColor = empty?'var(--bad-fg)':low?'var(--warn-fg)':'var(--ok-fg)';
                              return (
                                <button key={slot.id} type="button" disabled={isPast} onClick={()=>toggleSlot(slot.id)}
                                  className={`sb-slot ${isPast?'disabled':''} ${isSelected?'sel':''}`}
                                  style={{ cursor:isPast?'not-allowed':'pointer', fontSize:12 }}>
                                  <span style={{ fontWeight:600, fontSize:13, textDecoration:isPast?'line-through':'none' }}>
                                    {slot.start_time || slot.time}
                                  </span>
                                  {rentalCatalogue.length > 0 && !isPast && lowest!=null && (
                                    <div style={{ display:'flex', flexDirection:'column', gap:3, marginTop:5 }}>
                                      <div className="sb-bar" style={{ height:4, background: isSelected?'color-mix(in oklch,var(--brand-ink) 25%,transparent)':'var(--line-soft)' }}>
                                        <i style={{ width:`${Math.round((Math.max(0,lowest)/maxStock)*100)}%`, background: isSelected?'var(--brand-ink)':barColor }} />
                                      </div>
                                      <span style={{ fontSize:10, fontWeight:600,
                                        color: isSelected ? 'color-mix(in oklch,var(--brand-ink) 80%,transparent)' : (empty?'var(--bad-fg)':low?'var(--warn-fg)':'var(--ink-3)') }}>
                                        {empty ? 'none left' : `${lowest} left`}
                                      </span>
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ── Rental matrix ──────────────────────────────────────────── */}
            {rentalCatalogue.length > 0 && (
              <div className="sb-card" style={{ padding:'20px 22px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}>
                  <ShoppingBag size={15} style={s.accent}/>
                  <h2 style={s.sec}>
                    Rental Quantities
                    <span style={{ ...s.ink3, fontWeight:400, fontSize:12.5, marginLeft:8 }}>per hour</span>
                  </h2>
                </div>

                {uniqueSlotIds.length === 0 ? (
                  <p style={{ fontSize:13,...s.ink4,fontStyle:'italic' }}>Select hours above to set quantities per slot.</p>
                ) : (
                  <div style={{ overflowX:'auto', margin:'0 -22px', padding:'0 22px' }}>
                    <table style={{ width:'100%', minWidth:'max-content', borderCollapse:'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign:'left', fontSize:11,...s.ink3, padding:'0 16px 10px 0', width:72 }}>Hour</th>
                          {rentalCatalogue.map(item => (
                            <th key={item.item_id} style={{ textAlign:'center', padding:'0 8px 10px', fontSize:12, fontWeight:600 }}>
                              <div>{item.name}</div>
                              <div style={{ fontSize:10,...s.ink3,fontWeight:400,...s.mono }}>₹{item.price_per_session}/hr · stock {item.quantity}</div>
                            </th>
                          ))}
                          <th style={{ width:80, textAlign:'center', padding:'0 0 10px 8px' }}>
                            <span style={{ fontSize:10,...s.ink4 }}>Copy all</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {uniqueSlotIds.map((slotId, rowIdx) => {
                          const isFirst = rowIdx === 0;
                          const rowHasValues = rentalCatalogue.some(item => getMatrixQty(slotId, item.item_id) > 0);
                          return (
                            <tr key={slotId} style={{ background:rowIdx%2===0?'var(--surface-2)':'transparent' }}>
                              <td style={{ padding:'8px 16px 8px 0', whiteSpace:'nowrap' }}>
                                <span style={{ fontFamily:'var(--f-mono)', fontSize:12.5, fontWeight:600 }}>
                                  {slotStart(slotId)}
                                </span>
                              </td>
                              {rentalCatalogue.map(item => {
                                const maxQty = slotStock(slotId, item);
                                const qty    = Math.min(getMatrixQty(slotId, item.item_id), maxQty);
                                const empty  = maxQty === 0;
                                return (
                                  <td key={item.item_id} style={{ padding:'6px 8px', textAlign:'center' }}>
                                    {empty ? (
                                      <span style={{ fontSize:11,color:'var(--bad-fg)' }} title="No stock available">Full</span>
                                    ) : (
                                      <div style={{ display:'inline-flex', alignItems:'center', gap:5 }}>
                                        <button type="button"
                                          onClick={() => setMatrixQty(slotId, item.item_id, qty-1)}
                                          disabled={qty===0}
                                          style={{ width:22,height:22,borderRadius:'50%',border:'1px solid var(--line)',background:'var(--surface)',cursor:qty===0?'not-allowed':'pointer',display:'grid',placeItems:'center',opacity:qty===0?.3:1,color:'var(--ink-2)' }}>
                                          <Minus size={9}/>
                                        </button>
                                        <span style={{ fontFamily:'var(--f-mono)', fontWeight:600, fontSize:13, minWidth:18, textAlign:'center',
                                          color:qty>0?'var(--ink)':'var(--ink-4)' }}>
                                          {qty}
                                        </span>
                                        <button type="button"
                                          onClick={() => setMatrixQty(slotId, item.item_id, qty+1)}
                                          disabled={qty>=maxQty}
                                          style={{ width:22,height:22,borderRadius:'50%',border:'1px solid var(--line)',background:'var(--surface)',cursor:qty>=maxQty?'not-allowed':'pointer',display:'grid',placeItems:'center',opacity:qty>=maxQty?.3:1,color:'var(--ink-2)' }}>
                                          <Plus size={9}/>
                                        </button>
                                      </div>
                                    )}
                                    {maxQty > 0 && (
                                      <div style={{ fontSize:10,...s.ink4,marginTop:2 }}>{maxQty} avail</div>
                                    )}
                                  </td>
                                );
                              })}
                              {/* Copy row to all */}
                              <td style={{ padding:'6px 0 6px 8px', textAlign:'center' }}>
                                {rowHasValues && (
                                  <button type="button"
                                    onClick={() => copyRowToAll(slotId)}
                                    className="sb-btn sm ghost"
                                    title="Copy this row's quantities to all selected hours"
                                    style={{ padding:'4px 8px', fontSize:10.5 }}>
                                    <Copy size={10}/> All
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      {/* Column totals */}
                      {uniqueSlotIds.length > 1 && (
                        <tfoot>
                          <tr style={{ borderTop:'1px solid var(--line-soft)' }}>
                            <td style={{ padding:'8px 16px 4px 0', fontSize:11,...s.ink3 }}>Total/hr</td>
                            {rentalCatalogue.map(item => {
                              const colTotal = uniqueSlotIds.reduce((s,sid)=>s+getMatrixQty(sid,item.item_id),0);
                              const colRevenue = rentalCatalogue.find(i=>i.item_id===item.item_id)?.price_per_session * colTotal;
                              return (
                                <td key={item.item_id} style={{ padding:'8px 8px 4px', textAlign:'center' }}>
                                  {colTotal > 0 && (
                                    <>
                                      <div style={{ fontSize:12,fontWeight:600,...s.mono }}>{colTotal} units</div>
                                      <div style={{ fontSize:10.5,...s.accent,...s.mono }}>₹{colRevenue}</div>
                                    </>
                                  )}
                                </td>
                              );
                            })}
                            <td />
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                )}

                {/* Quick copy helper text */}
                {uniqueSlotIds.length > 1 && rentalCatalogue.some(item => uniqueSlotIds.some(sid=>getMatrixQty(sid,item.item_id)>0)) && (
                  <p style={{ fontSize:11,...s.ink4,marginTop:10 }}>
                    Tip: Use <strong>All</strong> button on any row to copy those quantities to every selected hour.
                  </p>
                )}
              </div>
            )}

            {/* Custom one-off rentals */}
            <div className="sb-card" style={{ padding:'20px 22px' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                <h2 style={s.sec}>One-off Rentals <span style={{ ...s.ink3,fontWeight:400,fontSize:12.5 }}>(optional)</span></h2>
              </div>

              {customRentals.length === 0 && rentalCatalogue.length === 0 && (
                <p style={{ fontSize:12,...s.ink4,fontStyle:'italic',marginBottom:10 }}>
                  No rental catalogue for this facility. Use one-off items below.
                </p>
              )}

              {customRentals.length > 0 && (
                <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:10 }}>
                  {customRentals.map(r => (
                    <div key={r.id} style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <input type="text" value={r.name} onChange={e=>updateCustomRental(r.id,'name',e.target.value)}
                        placeholder="Item name" style={{ ...s.inp, padding:'6px 10px', fontSize:13 }} />
                      <div style={{ display:'flex',alignItems:'center',gap:4,background:'var(--surface-2)',border:'1px solid var(--line)',borderRadius:8,padding:'6px 10px' }}>
                        <span style={{ ...s.ink3,fontSize:13 }}>₹</span>
                        <input type="number" min="0" value={r.price} onChange={e=>updateCustomRental(r.id,'price',e.target.value)}
                          placeholder="0" style={{ width:56,background:'transparent',border:'none',outline:'none',fontSize:13,color:'var(--ink)' }} />
                      </div>
                      <div style={{ display:'flex',alignItems:'center',gap:5 }}>
                        <button type="button" onClick={()=>updateCustomRental(r.id,'qty',Math.max(1,(r.qty||1)-1))} className="sb-btn sm ghost icon" style={{ borderRadius:'50%',width:24,height:24,padding:0 }}><Minus size={10}/></button>
                        <span style={{ ...s.mono,fontSize:13,width:16,textAlign:'center' }}>{r.qty||1}</span>
                        <button type="button" onClick={()=>updateCustomRental(r.id,'qty',(r.qty||1)+1)} className="sb-btn sm ghost icon" style={{ borderRadius:'50%',width:24,height:24,padding:0 }}><Plus size={10}/></button>
                      </div>
                      <button type="button" onClick={()=>removeCustomRental(r.id)} className="sb-btn sm ghost icon" style={{ color:'var(--bad-fg)' }}><X size={13}/></button>
                    </div>
                  ))}
                </div>
              )}

              <button type="button" onClick={addCustomRental} className="sb-btn sm ghost" style={s.brand}>
                <Plus size={11}/> Add one-off item
              </button>
            </div>
          </div>

          {/* ── Right column ─────────────────────────────────────────────── */}
          <div className="l-aside-sticky" style={{ display:'flex', flexDirection:'column', gap:16 }}>

            {/* Summary */}
            <div className="sb-card" style={{ padding:'18px 20px' }}>
              <div className="sb-card-lbl" style={{ marginBottom:12 }}>Rental Summary</div>

              {uniqueSlotIds.length === 0 ? (
                <p style={{ fontSize:13,...s.ink4,fontStyle:'italic' }}>Select hours from the slot picker.</p>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {/* Hours selected */}
                  <div style={{ padding:'10px 12px', background:'color-mix(in oklch,var(--accent) 8%,transparent)', border:'1px solid color-mix(in oklch,var(--accent) 20%,transparent)', borderRadius:9 }}>
                    <p style={{ fontSize:13,fontWeight:600,...s.accent,margin:0 }}>
                      {rentalHours} hour{rentalHours>1?'s':''} selected
                    </p>
                    <p style={{ fontSize:11,...s.ink3,margin:'4px 0 0',...s.mono }}>
                      {uniqueSlotIds.map(sid=>slotStart(sid)).join(', ')}
                    </p>
                  </div>

                  {/* Per-item totals */}
                  {rentalCatalogue.some(item=>uniqueSlotIds.some(sid=>getMatrixQty(sid,item.item_id)>0)) && (
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                      {rentalCatalogue.filter(item=>uniqueSlotIds.some(sid=>getMatrixQty(sid,item.item_id)>0)).map(item => {
                        const totalQty = uniqueSlotIds.reduce((s,sid)=>s+getMatrixQty(sid,item.item_id),0);
                        const revenue  = totalQty * item.price_per_session;
                        return (
                          <div key={item.item_id} style={{ display:'flex', justifyContent:'space-between', fontSize:12.5 }}>
                            <span>{item.name} ×{totalQty}</span>
                            <span style={s.mono}>₹{revenue}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {customTotal > 0 && (
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:12.5 }}>
                      <span style={s.ink3}>One-off items</span>
                      <span style={s.mono}>₹{customTotal}</span>
                    </div>
                  )}

                  {grandTotal > 0 && (
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:15, fontWeight:600, paddingTop:8, borderTop:'1px solid var(--line-soft)' }}>
                      <span>Total to collect</span>
                      <span style={{ ...s.accent,...s.mono }}>₹{grandTotal}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Payment */}
            <div className="sb-card" style={{ padding:'18px 20px', display:'flex', flexDirection:'column', gap:14 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div className="sb-card-lbl" style={{ margin:0 }}>Payment</div>
                <span style={{ fontSize:11,...s.ink3 }}>optional</span>
              </div>

              {grandTotal > 0 && (
                <div style={{ display:'flex', gap:6 }}>
                  <button type="button" onClick={()=>setPayment(p=>({...p,amount:String(grandTotal),status:'Paid'}))}
                    style={{ flex:1,padding:'8px 10px',fontSize:11.5,fontWeight:600,borderRadius:8,background:'color-mix(in oklch,var(--ok-fg) 10%,transparent)',border:'1px solid color-mix(in oklch,var(--ok-fg) 25%,transparent)',color:'var(--ok-fg)',cursor:'pointer' }}>
                    ✓ Full · ₹{grandTotal}
                  </button>
                  <button type="button" onClick={()=>setPayment(p=>({...p,amount:'',status:'Pending'}))} className="sb-btn sm ghost">Pending</button>
                </div>
              )}

              <div style={s.row2}>
                <div className="sb-field">
                  <label>Method</label>
                  <div style={{ display:'flex', gap:6 }}>
                    {['Cash','UPI'].map(m => (
                      <button key={m} type="button" onClick={()=>setPayment({...payment,method:m})}
                        style={{ flex:1,padding:'7px 10px',borderRadius:8,fontSize:13,fontWeight:500,border:`1px solid ${payment.method===m?'var(--accent)':'var(--line)'}`,background:payment.method===m?'var(--accent)':'var(--surface-2)',color:payment.method===m?'var(--accent-ink)':'var(--ink-2)',cursor:'pointer' }}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sb-field">
                  <label>Amount (₹)</label>
                  <input type="number" value={payment.amount} onChange={e=>setPayment({...payment,amount:e.target.value})} style={s.inp} placeholder="Leave blank if not yet" />
                </div>
              </div>

              {payment.amount && (
                <div style={s.row2}>
                  <div className="sb-field">
                    <label>Status</label>
                    <select value={payment.status} onChange={e=>setPayment({...payment,status:e.target.value})} style={s.inp}>
                      <option>Pending</option><option>Paid</option><option>Refunded</option>
                    </select>
                  </div>
                  {payment.method==='UPI' && (
                    <div className="sb-field">
                      <label>UPI Txn ID</label>
                      <input type="text" value={payment.transaction_id} onChange={e=>setPayment({...payment,transaction_id:e.target.value})} style={s.inp} />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Submit */}
            {progress ? (
              <div style={{ padding:'8px 0' }}>
                <div style={{ background:'var(--line-soft)',borderRadius:999,height:4,overflow:'hidden',marginBottom:8 }}>
                  <div style={{ background:'var(--accent)',height:4,borderRadius:999,transition:'width .3s',width:`${Math.round((progress.done/progress.total)*100)}%` }} />
                </div>
                <p style={{ textAlign:'center',fontSize:13,...s.ink3 }}>Creating booking… {progress.done} / {progress.total}</p>
              </div>
            ) : (
              <button type="submit" disabled={selectedSlots.size===0} className="sb-btn primary" style={{ width:'100%',justifyContent:'center',padding:'11px 16px',fontSize:14 }}>
                {submitLabel}
              </button>
            )}
          </div>
        </div>
      </form>

      <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
    </div>
  );
}
